import React, { useCallback } from "react";
import {
  useWSModalCustom,
  WSCard,
  WSDataItem,
  WSList,
  WSModal,
  WSModalCustomProps,
  WSText
} from "@wingspanhq/fe-component-library";
import { BulkActionReminder } from "../components/BulkActionReminder";
import { BulkActionType } from "../types";

type ConfirmationLineItem = {
  label: string;
  value: string;
};

type Props = {
  actionName: string;
  actionType: BulkActionType;
  itemCount: number;
  description?: string;
  lineItems?: ConfirmationLineItem[];
  confirmLabel?: string;
  confirmDestructive?: boolean;
} & WSModalCustomProps<boolean>;

const ModalBulkActionConfirmation: React.FC<Props> = props => {
  const handleCancel = useCallback(() => {
    props.onClose(false);
  }, [props]);

  const handleConfirm = useCallback(() => {
    props.onClose(true);
  }, [props]);

  return (
    <WSModal
      title={props.actionName}
      onClose={() => handleCancel()}
      divider
      actions={{
        alignment: "fill",
        buttons: [
          {
            kind: "Secondary",
            label: "No, cancel",
            onClick: handleCancel,
            type: "button"
          },
          {
            kind: "Primary",
            label:
              props.confirmLabel ?? `Yes, ${props.actionName.toLowerCase()}`,
            destructive: props.confirmDestructive,
            onClick: handleConfirm,
            type: "button"
          }
        ]
      }}
    >
      <WSList gap="M">
        <WSText.ParagraphSm color="gray500">
          {props.description ??
            `Are you sure you want to ${props.actionName.toLowerCase()} ${props.itemCount.toLocaleString()} items?`}
        </WSText.ParagraphSm>

        {props.lineItems && props.lineItems.length > 0 && (
          <WSCard>
            {props.lineItems.map(item => (
              <WSDataItem
                key={item.label}
                inline
                label={item.label}
                value={item.value}
              />
            ))}
          </WSCard>
        )}

        <BulkActionReminder
          actionName={props.actionName}
          actionType={props.actionType}
        />
      </WSList>
    </WSModal>
  );
};

export const useModalBulkActionConfirmation = () =>
  useWSModalCustom(ModalBulkActionConfirmation);
